import React, { useState, useRef, useCallback, useEffect } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { useDrag, useDrop } from "react-dnd";
import update from "immutability-helper";
import { ColorPicker } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";
import { uuidv4 } from "../../../Hooks/Display/utils";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";

export const DraggableCard = ({ id, text, color, onColorChange, showColorPicker, showLanguageTranslation, index, onItemDragged, onItemEdited, onItemRemoved, onEnterKeyIsPressed, isFocused, duplicate, onEditLanguages }) => {
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: "DraggableCard",
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},

		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) { return; }
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) { return; }
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) { return; }
			// Time to actually perform the action
			onItemDragged(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});

	const paletteSettings = {
		palette: [
			"#222222", "#292e6b", "#009ccc", "#00643a", "#339947", "#fbbc3d",
			"#db552c", "#7f1725", "#ec008c", "#5c197b", "#51399f", "#444444",
			"#1b478b", "#43b4d5", "#207752", "#60af49", "#fbd144", "#e87025",
			"#b20b1e", "#ef33a3", "#71338d", "#6951aa", "#666666", "#0d60ab",
			"#86cdde", "#56987d", "#8dc54b", "#fbe74b", "#f58b1f", "#e60017",
			"#f266ba", "#9260a1", "#8874c2", "#888888", "#007acc", "#c9e7e7",
			"#7caf9a", "#a8ce4b", "#fbfd52", "#f7a24b", "#eb3345", "#f599d1",
			"#ae88b9", "#aa9cdf", "#aaaaaa", "#3f9bd8", "#d6eded", "#9cc3b2",
			"#c3d84c", "#fcfd7d", "#f9b978", "#f06673", "#f9cce8", "#c7abd0",
			"#c0b6e9", "#cccccc", "#7fbce5", "#e4f3f3", "#bfd8cd", "#d7e587",
			"#fcfea8", "#fbd0a5", "#f599a2", "#fbddef", "#e0cae7", "#dad4f7"
		],
		columns: 11,
		tileSize: 16,
	};

	const [{ isDragging }, drag] = useDrag({
		type: "DraggableCard",
		item: () => { return { id, index }; },
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	drag(drop(ref));

	const checkEnterKeyPressed = (e) => {
		if (e.key === 'Enter')
			onEnterKeyIsPressed();
	};

	return (
		<div ref={ref} data-handler-id={handlerId} className="my-1 rounded draggable-row d-flex align-items-center">
			<i className="me-3 fas fa-grip-vertical grabbable"></i>
			<input
				type="text"
				className={"form-control " + (duplicate ? 'border-danger is-invalid' : '')}
				value={text}
				autoFocus={isFocused}
				onKeyDown={(e) => checkEnterKeyPressed(e)}
				onChange={(e) => onItemEdited(e.target.value, index)}
			/>
			{showColorPicker &&
				<span className="ms-2">
					<ColorPicker
						paletteSettings={paletteSettings}
						size={"medium"}
						value={color}
						format={"hex"}
						onChange={(e) => onColorChange(e.value, index)}
					/>
				</span>
			}
			{showLanguageTranslation &&
				<button
					type="button"
					className="btn btn-secondary mx-2"
					onClick={() => onEditLanguages(index)}
				>
					<i className="fas fa-language"></i>
				</button>
			}
			<i className={`fa fa-times mx-3 pointer ${index === 0 && 'invisible'}`} onClick={() => onItemRemoved(index)}></i>
		</div>
	);
};

export const StateFeildOptionsField = (props) => {

	const { t } = useTranslation([
		"attribute/state_field",
	], {
		useSuspense: false
	});

	const {
		autoFocus,
		value,
		onChange,
		onErr,
		showColorPicker,
		showLanguageTranslation,
	} = props;
	const ref = useRef(null);

	const [collection, setCollection] = useState([]);
	const [focus, setFocus] = useState(autoFocus);
	const [color, setColor] = useState("rgba(255,255,255, 1)");
	const [val, setVal] = useState({
		"name": "",
		"color": color
	});
	const [showTranslationDialog, setShowTranslationDialog] = useState(false);
	const [currentEditIndex, setCurrentEditIndex] = useState(null);

	//done
	useEffect(() => {
		if (collection.length === 0 && value.length === 0) addToCollection()
		setFocus(autoFocus);
		setCollection(value);
	}, [props]);

	//done
	function handleChange(value) {
		onChange(value);
	}

	//done
	function addToCollection() {
		onErr("");
		const newValue = {
			"_id": uuidv4(),
			"order": collection.length > 0 ? parseInt((collection[collection.length - 1]["order"]) + 10000000000) : 10000000000,
			"name": "",
			"color": color
		}
		setCollection([...collection, [newValue]]);
		setVal(newValue);
		handleChange([...collection, ...[newValue]]);
		setFocus(true);
	}

	//done
	function itemDeleted(index) {
		onErr("");
		setCollection(collection.filter((o, ix) => index !== ix));
		handleChange(collection.filter((o, ix) => index !== ix));
	}

	//done
	function itemValueChanged(val, index) {
		onErr("");
		const updatedCollection = collection.map((o, ix) => {
			if (index === ix) {
				return {
					...o,
					name: val
				}
			}
			return o;
		});
		setCollection(updatedCollection);
		handleChange(updatedCollection);
	}

	const cardDragged = useCallback(
		(dragIndex, hoverIndex) => {
			const dragCard = collection[dragIndex];
			const v = update(collection, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, dragCard],
				],
			});
			setCollection(v);
			handleChange(v);
		},
		[collection]
	);

	const colorValueChanged = (val, index) => {
		const updatedCollection = collection.map((o, ix) => {
			if (index === ix) {
				return {
					...o,
					color: val
				}
			}
			return o;
		});
		setCollection(updatedCollection);
		handleChange(updatedCollection);
	};

	const handleEditLanguages = (index) => {
		setCurrentEditIndex(index);
		setShowTranslationDialog(true);
	};

	const handleCloseTranslationDialog = () => {
		setShowTranslationDialog(false);
		setCurrentEditIndex(null);
	};

	const editLanguages = (updatedLanguages, index) => {
		const updatedCollection = collection.map((o, ix) => {
			if (index === ix) {
				return {
					...o,
					languages: updatedLanguages
				};
			}
			return o;
		});
		setCollection(updatedCollection);
		handleChange(updatedCollection);
		setShowTranslationDialog(false);
		setCurrentEditIndex(null);
	};

	return (
		<div className="inner-drop-container">
			<DndProvider backend={HTML5Backend}>
				{collection && collection.map((o, i) => (
					<DraggableCard
						key={i}
						index={i}
						id={o.id}
						text={o.name}
						color={o.color}
						showLanguageTranslation={showLanguageTranslation}
						showColorPicker={showColorPicker}
						onColorChange={colorValueChanged}
						onItemDragged={cardDragged}
						onItemEdited={itemValueChanged}
						onItemRemoved={itemDeleted}
						onEnterKeyIsPressed={addToCollection}
						duplicate={collection.filter(x => x.name === o.name && x.name !== "").length > 1}
						isFocused={i === collection.length - 1 && focus}
						onEditLanguages={handleEditLanguages}
					/>
				))}
			</DndProvider>
			<button type="button" className="btn p-2 hover-bg-7 hover-border-0 form-label d-inline-block t-primary pointer" onClick={() => addToCollection()}>
				<i className="me-1 fa fa-plus"></i>
				{t("add_states")}
			</button>

			{showTranslationDialog && currentEditIndex !== null && (
				<GenericTranslationDialog
					onClose={handleCloseTranslationDialog}
					onSave={(updatedLanguages) => editLanguages(updatedLanguages, currentEditIndex)}
					languages={collection[currentEditIndex]?.languages || []}
					project={props.project}
					fields={["name"]}
					type={"State"}
				/>
			)}
		</div>
	);
};
